import {
    DashboardPeriod,
    DashboardStats,
    PERIOD_LABELS,
} from '@/lib/api/dashboard/dashboard-api'
import {
    MdAssignment,
    MdAttachMoney,
    MdLocalHospital,
    MdMedicalServices,
    MdMedication,
    MdMonitorHeart,
    MdPeople,
    MdSupportAgent,
} from 'react-icons/md'
import StatCard from './stat-card'
import { useServiceLabel } from '@/lib/hooks/use-service-label'

interface QuickStatsProps {
    stats: DashboardStats
    isHealth: boolean
    isIntermediation: boolean
    period: DashboardPeriod
}

const fmt = (n: number) => n.toLocaleString('pt-BR')

export default function QuickStats({
    stats,
    isHealth,
    isIntermediation,
    period,
}: QuickStatsProps) {
    const serviceLabels = useServiceLabel()
    const generalCards = [
        {
            title: 'Atendimentos',
            value: stats.attendancesPeriod,
            subtitle: `Total acumulado: ${fmt(stats.totalAttendances)}`,
            growth: stats.attendancesGrowth,
            icon: MdSupportAgent,
            color: 'primary',
            href: '/atendimento/chats',
        },
        {
            title: 'Clientes',
            value: stats.newClientsPeriod,
            subtitle: `Total acumulado: ${fmt(stats.totalClients)}`,
            growth: stats.clientsGrowth,
            icon: MdPeople,
            color: 'secondary',
            href: '/person/clients',
        },
        {
            title: `Receita (${PERIOD_LABELS[period]})`,
            value: stats.revenuePeriod,
            subtitle: `Ticket médio: ${(stats.averageTicket / 100).toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            })}`,
            growth: stats.revenueGrowth,
            icon: MdAttachMoney,
            color: 'success',
            format: 'currency' as const,
        },
    ]

    const health = stats.health
    const healthCards = health
        ? [
            {
                title: serviceLabels.Consultations,
                value: health.consultationsPeriod,
                subtitle: `Total acumulado: ${fmt(health.totalConsultations)}`,
                growth: health.consultationsGrowth,
                icon: MdLocalHospital,
                color: 'info',
                href: '/bookings/consultations',
            },
            {
                title: serviceLabels.Orders,
                value: health.ordersPeriod,
                subtitle: `Total acumulado: ${fmt(health.totalOrders)}`,
                growth: health.ordersGrowth,
                icon: MdMedicalServices,
                color: 'accent',
                href: '/bookings/orders',
            },
            {
                title: 'Exames',
                value: health.examsPeriod,
                subtitle: `${fmt(health.pendingExams)} pendentes`,
                growth: health.examsGrowth,
                icon: MdAssignment,
                color: 'warning',
                href: '/bookings/exams',
            },
            // Receituários e Triagens nao se aplicam a empresas de intermediação.
            ...(!isIntermediation
                ? [
                    {
                        title: 'Receituários',
                        value: health.prescriptionsPeriod,
                        subtitle: `Total acumulado: ${fmt(health.totalPrescriptions)}`,
                        growth: health.prescriptionsGrowth,
                        icon: MdMedication,
                        color: 'error',
                        href: '/medical/prescriptions',
                    },
                    {
                        title: 'Triagens',
                        value: health.triagesPeriod,
                        subtitle: `Total acumulado: ${fmt(health.totalTriages)}`,
                        growth: health.triagesGrowth,
                        icon: MdMonitorHeart,
                        color: 'primary',
                        href: '/bookings/triage',
                    },
                ]
                : []),
        ]
        : []

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {generalCards.map((card, index) => (
                    <StatCard key={index} {...card} />
                ))}
            </div>

            {isHealth && healthCards.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {healthCards.map((card, index) => (
                        <StatCard key={index} {...card} />
                    ))}
                </div>
            )}
        </div>
    )
}
